<template>
    <div>
        <div>
            <div ref="gridElement" :class="resultGridClass">
                <template v-for="(entry, index) in displayedEntries" :key="entry.objectID">
                    <CategoryBannerEntry v-if="bannerAtPosition(index)" :banner="bannerAtPosition(index)!" :max-cols="gridCols" />

                    <ProductCard
                        :carried-filter-params="categorySearch.carriedFilterParams.value"
                        :data-list-index="index"
                        :eager="index < eagerImageCount"
                        :fetch-priority="index < fetchPriorityImageCount"
                        :item="entry.item"
                        :wishlist-product="entry.wishlistProduct"
                        plain
                        show-discount
                        @click="handleClick(index, entry)"
                    />
                </template>

                <template v-for="index in skeletonCount" :key="`skeleton-${index}`">
                    <CategoryProductCardSkeleton />
                </template>

                <CategoryBannerEntry
                    v-if="bannerAtPosition(displayedEntries.length)"
                    :banner="bannerAtPosition(displayedEntries.length)!"
                    :max-cols="gridCols"
                />
            </div>
        </div>

        <div v-if="canLoadMore" class="mt-8 flex justify-center">
            <Button
                :label="$t('general.show_more_products')"
                :loading="isLoadingMore"
                class="border border-gray-300 text-gray-900 hover:border-gray-400"
                no-icon
                pill
                type="button"
                @click="loadMore"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import Button from '@/components/buttons/Button.vue';
import ProductCard from '@/components/product/ProductCard.vue';
import { categoryProductImageSizes, type useCategorySearch } from '@/composables/category/useCategorySearch';
import { wishlistListKey, type WishlistProductData } from '@/composables/useWishlist';
import { pickVariantValues } from '@/lib/products/urlFilters';
import { useCategoryListIdentity } from '@/composables/category/useCategoryListIdentity';
import { hitSelectItemPayload, useSelectItemTracking, type SelectItemPayload } from '@/composables/category/useSelectItemTracking';
import { useViewItemListTracking } from '@/composables/category/useViewItemListTracking';
import { useVarifyInlineFilters } from '@/composables/category/useVarifyInlineFilters';
import CategoryBannerEntry from '@/pages/category/partials/CategoryBannerEntry.vue';
import CategoryProductCardSkeleton from '@/pages/category/partials/CategoryProductCardSkeleton.vue';
import { categoryProductStore } from '@/stores/category/CategoryProductStore';
import type { CategoryGridEntry } from '@/types/category/CategoryGridEntry';
import type { CategorySearchEventSender, CategorySearchResult } from '@/types/category/CategorySearchTypes';
import type { CategoryBannerObject, CategoryInitialResultObject } from '@/types/generated';
import { useBreakpoints } from '@vueuse/core';
import { computed, onMounted, provide, ref, watch } from 'vue';

/** Cards above the fold; these load eagerly instead of lazily. */
const eagerImageCount = 6;
/** Only the first couple of cards get the LCP fetchpriority hint, matching the preload link. */
const fetchPriorityImageCount = 2;

const props = defineProps<{
    categorySearch: ReturnType<typeof useCategorySearch>;
    hits: CategorySearchResult[];
    /** True once the hits slot has run. */
    hasPublishedHits: boolean;
    initialResults: CategoryInitialResultObject[];
    /** False when the server payload does not match the URL's filters. */
    trustInitialResults: boolean;
    nbHits: number;
    sendEvent?: CategorySearchEventSender;
}>();

const productStore = categoryProductStore();
const { trackSelectItem } = useSelectItemTracking();

provide(wishlistListKey, useCategoryListIdentity);

const initialEntries = computed((): CategoryGridEntry[] =>
    props.initialResults.map((hit: CategoryInitialResultObject): CategoryGridEntry => ({
        objectID: hit.object_id,
        taggrsCategories: hit.taggrs_categories,
        item: {
            name: hit.name,
            image_url: hit.image_url,
            image_sizes: categoryProductImageSizes,
            image_srcset: hit.image_srcset,
            previews: hit.previews,
            url_key: hit.url,
            price: hit.price,
            recommended_price: hit.recommended_price,
            discount_percentage: hit.discount_percentage,
        },
    })),
);

// Resolved once per hits array so ProductCard keeps a stable prop identity.
const algoliaEntries = computed((): CategoryGridEntry[] =>
    props.hits.map((hit: CategorySearchResult): CategoryGridEntry => {
        const resolved = props.categorySearch.asCategorySearchResult(hit);

        return {
            objectID: resolved.objectID,
            taggrsCategories: resolved.taggrs_categories ?? [],
            item: {
                name: resolved.name,
                image_url: resolved.image_url,
                image_sizes: categoryProductImageSizes,
                image_srcset: resolved.image_srcset,
                previews: resolved.previews,
                url_key: resolved.url_key,
                selected_sku: resolved.selected_sku,
                sku: resolved.sku,
                price: resolved.price,
                recommended_price: resolved.recommended_price,
                discount_percentage: resolved.discount_percentage,
                ...pickVariantValues(resolved),
            },
            hit,
            wishlistProduct: wishlistProductForHit(resolved),
        };
    }),
);

// Without a variant filter a hit has no selected_sku; its sku is then the default variant, as in select_item tracking.
function wishlistProductForHit(resolved: CategorySearchResult): WishlistProductData | undefined {
    const selectedSku = resolved.selected_sku ?? resolved.sku;

    if (!selectedSku || !resolved.url_key || !resolved.name) {
        return undefined;
    }

    return {
        selectedSku,
        configurableProductSku: resolved.objectID,
        urlKey: resolved.url_key,
        productName: resolved.name,
        price: resolved.price ?? 0,
        thumbnail: resolved.thumbnail_url ?? resolved.image_url,
        recommendedPrice: resolved.recommended_price,
        discount: resolved.discount_percentage ?? null,
    };
}

const entries = computed((): CategoryGridEntry[] => {
    if (props.hasPublishedHits) {
        return algoliaEntries.value;
    }

    return props.trustInitialResults ? initialEntries.value : [];
});

/** Enough to fill the viewport until the first response arrives. */
const skeletonPlaceholderCount = 12;

const skeletonCount = computed((): number => (entries.value.length > 0 || props.hasPublishedHits ? 0 : skeletonPlaceholderCount));

const canLoadMore = computed((): boolean => entries.value.length > 0 && entries.value.length < props.nbHits);

const isLoadingMore = ref<boolean>(false);

function loadMore(): void {
    isLoadingMore.value = true;
    props.categorySearch.loadMoreResults();
}

// Not on the hits array itself: InstantSearch hands a new one as soon as a search starts.
watch(
    () => `${entries.value.length}|${props.nbHits}`,
    (): void => {
        isLoadingMore.value = false;
    },
);

const breakpoints = useBreakpoints({ lg: 1024 });
const isDesktop = breakpoints.greaterOrEqual('lg');
const isMounted = ref(false);

onMounted(() => {
    isMounted.value = true;
});

// Before mount, default to desktop to match SSR output (provideSSRWidth uses 1280px).
const useDesktop = computed((): boolean => (isMounted.value ? isDesktop.value : true));

const bannerColSpan = (banner: CategoryBannerObject): number => {
    const size = useDesktop.value ? banner.desktop_size : banner.mobile_size;
    const max = useDesktop.value ? gridCols.value.desktop : gridCols.value.mobile;

    return Math.min(size, max);
};

// Banners occupy grid cells, so drop as many products from the end as the visible banners span
const displayedEntries = computed((): CategoryGridEntry[] => {
    if (entries.value.length >= props.nbHits) {
        return entries.value;
    }

    const bannerCellCount = props.categorySearch.categoryBanners.value
        .filter((banner: CategoryBannerObject) => banner.position <= entries.value.length)
        .reduce((total: number, banner: CategoryBannerObject) => total + bannerColSpan(banner), 0);

    return entries.value.slice(0, Math.max(0, entries.value.length - bannerCellCount));
});

// Desktop shows four columns; next to the Varify inline filters the grid toggle picks three or two, as before the redesign.
const resultGridClass = computed(() => [
    'grid gap-x-4 gap-y-5 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-10 varify-inline-filters:lg:gap-x-4 varify-inline-filters:lg:gap-y-5',
    productStore.gridView ? 'grid-cols-2 varify-inline-filters:lg:grid-cols-3' : 'grid-cols-1 varify-inline-filters:lg:grid-cols-2',
]);

const isInlineFilters = useVarifyInlineFilters();

const inlineFiltersGridViewCols = 3;

const gridCols = computed(() => {
    const mobile = productStore.gridView ? 2 : 1;

    if (!isInlineFilters.value) {
        return { mobile, desktop: 4 };
    }

    return { mobile, desktop: productStore.gridView ? inlineFiltersGridViewCols : 2 };
});

const gridElement = ref<HTMLElement | undefined>(undefined);

function selectItemPayload(entry: CategoryGridEntry): SelectItemPayload {
    if (entry.hit) {
        return hitSelectItemPayload(props.categorySearch.asCategorySearchResult(entry.hit));
    }

    return {
        itemId: entry.objectID,
        itemName: entry.item.name ?? entry.objectID,
        price: entry.item.price,
        categories: entry.taggrsCategories,
    };
}

const { flushImpressions } = useViewItemListTracking({
    container: gridElement,
    payloadAt: (index: number): SelectItemPayload | undefined => {
        const entry = displayedEntries.value[index];

        return entry ? selectItemPayload(entry) : undefined;
    },
    entries: displayedEntries,
});

const bannerAtPosition = (index: number): CategoryBannerObject | undefined =>
    props.categorySearch.categoryBanners.value.find((banner: CategoryBannerObject) => banner.position === index + 1);

// Pending impressions go out first, so the list view precedes the click in the dataLayer.
function handleClick(index: number, entry: CategoryGridEntry): void {
    flushImpressions();

    if (entry.hit) {
        props.categorySearch.handleResultClick(index, entry.hit, props.sendEvent);

        return;
    }

    trackSelectItem(index, selectItemPayload(entry));
}
</script>
